import { action, makeObservable, observable, runInAction, toJS } from "mobx"

import TPAPI from "@/api/tracking-page"
import { ThemeLayoutEnum } from "@/models/ITrackingPageOptions"
import type {
  TrackingPageThemeBackdropContent,
  TrackingPageThemeBackdropStyle,
  TrackingPageThemeConfigEnvelopeData,
  TrackingPageThemeConfigGetData,
  TrackingPageThemeConfigSaveResponse,
  TrackingPageThemeId,
  TrackingPageThemeLayoutInlinePayload,
  TrackingPageThemePreview,
  TrackingPageThemeShowcaseContent,
  TrackingPageThemeShowcaseStyle,
} from "@/models/ITrackingPageThemeLayoutConfig"
import {
  pruneMissingShopifyMediaImagesInThemePayload,
  removeShopifyMediaImageGidsFromPayload,
} from "@/utils/pruneThemeLayoutMissingShopifyMediaImages"
import { normalizeThemeLayoutPayloadImagesFromApi } from "@/utils/themeLayoutThemeImageField"

const normalizeThemeId = (theme: TrackingPageThemeId): TrackingPageThemeId =>
  (theme === 2 ? 2 : 3)

/** 将 GET / 保存返回的多种形态规整为编辑器使用的内联 `{ theme, content, style }` */
export const normalizeTrackingPageThemeConfigGetData = (
  data: TrackingPageThemeConfigGetData,
): TrackingPageThemeLayoutInlinePayload | undefined => {
  const isShowcaseContent = (
    content: TrackingPageThemeBackdropContent | TrackingPageThemeShowcaseContent,
  ): content is TrackingPageThemeShowcaseContent => "columns" in content

  const isShowcaseStyle = (
    style: TrackingPageThemeBackdropStyle | TrackingPageThemeShowcaseStyle,
  ): style is TrackingPageThemeShowcaseStyle => "width" in style.order_lookup_widget

  const isBackdropStyle = (
    style: TrackingPageThemeBackdropStyle | TrackingPageThemeShowcaseStyle,
  ): style is TrackingPageThemeBackdropStyle => "layout" in style.order_lookup_widget

  const payload = "tracking_page_theme_config" in data
    ? data.tracking_page_theme_config
    : data

  if ("theme_config" in payload) {
    const { content, style } = payload.theme_config

    return normalizeThemeLayoutPayloadImagesFromApi({
      theme: normalizeThemeId(payload.theme),
      content,
      style,
    })
  }

  if ("content" in payload && "style" in payload) {
    if (isShowcaseContent(payload.content)) {
      if (!isShowcaseStyle(payload.style)) {
        return undefined
      }

      return normalizeThemeLayoutPayloadImagesFromApi({
        theme: normalizeThemeId(payload.theme),
        content: payload.content,
        style: payload.style,
      })
    }

    if (!isBackdropStyle(payload.style)) {
      return undefined
    }

    const inline = {
      theme: normalizeThemeId(payload.theme),
      content: payload.content,
      style: payload.style,
    }

    return normalizeThemeLayoutPayloadImagesFromApi(inline)
  }

  return undefined
}

export const normalizeSavedThemeConfigPayload = (
  resp: TrackingPageThemeConfigSaveResponse,
): TrackingPageThemeConfigGetData | undefined => {
  const root = resp as unknown as Record<string, unknown>

  if (root.tracking_page_theme_config !== undefined && root.tracking_page_theme_config !== null) {
    return { tracking_page_theme_config: root.tracking_page_theme_config } as TrackingPageThemeConfigEnvelopeData
  }

  const raw = resp.data

  if (!raw || typeof raw !== "object") {
    return undefined
  }

  if ("tracking_page_theme_config" in raw && raw.tracking_page_theme_config !== undefined) {
    return { tracking_page_theme_config: raw.tracking_page_theme_config } as TrackingPageThemeConfigEnvelopeData
  }

  return raw as TrackingPageThemeConfigGetData
}

/**
 * 主题布局定制页（Backdrop / Showcase）的 `theme_config` 接口数据。
 */

export default class ThemeLayoutConfigStore {
  @observable public backdropConfig: TrackingPageThemeLayoutInlinePayload | undefined

  @observable public showcaseConfig: TrackingPageThemeLayoutInlinePayload | undefined

  @observable public currentTheme: TrackingPageThemeId | undefined

  @observable public loading = false

  /** 各主题布局的预览链接列表（`/v2/tracking_page/theme_config/preview_list`） */
  @observable.ref public previewList: TrackingPageThemePreview[] = []

  constructor() {
    makeObservable(this)
  }

  @action.bound
  public reset(): void {
    this.backdropConfig = undefined
    this.showcaseConfig = undefined
    this.currentTheme = undefined
    this.loading = false
    this.previewList = []
  }

  /** 按 `ThemeLayoutEnum` 取对应布局的预览链接（`id` 与枚举对齐） */
  public getPreviewUrl(theme: ThemeLayoutEnum): string | undefined {
    return this.previewList.find((item) => item.id === theme)?.url
  }

  /**
   * 拉取全部主题布局的预览链接列表，供 `ThemeLayoutSelector` 的缩略图 iframe 使用。
   */
  @action.bound
  public async fetchThemePreviewList(): Promise<void> {
    const { data } = await TPAPI.getThemePreviewList()
    const previews = data?.tracking_page_theme_previews

    if (!Array.isArray(previews)) {
      return
    }

    runInAction(() => {
      this.previewList = previews
    })
  }

  @action.bound
  public setBackdropConfig(config: TrackingPageThemeLayoutInlinePayload): void {
    this.backdropConfig = config
    this.currentTheme = config.theme
  }

  @action.bound
  public setShowcaseConfig(config: TrackingPageThemeLayoutInlinePayload): void {
    this.showcaseConfig = config
    this.currentTheme = config.theme
  }

  /**
   * 按编辑器变体拉取 `tracking_page/theme_config/:theme`（进入定制页时应调用）。
   */
  @action.bound
  public async fetchThemeConfigByVariant(variant: ThemeLayoutEnum): Promise<void> {
    const isBackdrop = variant === ThemeLayoutEnum.BACKDROP

    this.loading = true

    try {
      const { data: envelope } = await TPAPI.getThemeConfig(variant)
      const normalizedPayload = normalizeTrackingPageThemeConfigGetData(envelope)

      if (!normalizedPayload) {
        return
      }

      // sync 仅就地剪除已删除图片的引用，不调用 save：data 与基线（原配置）都用剪枝结果，
      // 这样不会触发全局保存（ContextualSaveBar）。
      const { payload: prunedPayload } = await pruneMissingShopifyMediaImagesInThemePayload(
        normalizedPayload,
      )

      runInAction(() => {
        if (isBackdrop) {
          this.backdropConfig = prunedPayload
        } else {
          this.showcaseConfig = prunedPayload
        }
        this.currentTheme = prunedPayload.theme
      })
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  /** 同一时刻仅允许一次重新聚焦同步，避免 focus / visibilitychange 同时触发重复请求 */
  private syncingCurrentThemeImages = false

  /**
   * 用户从其他窗口/标签页切回定制页时调用：对当前内存中的配置走 /v4/common/images/sync，
   * 若引用的图片已在别处被删除，则就地移除这些引用。
   * 不重新拉取、不调用 save：仅修改内存 data；由调用方同步更新基线（原配置）以免触发全局保存。
   * @returns 是否发生了剪枝（true 时调用方需把基线更新为当前配置）
   */
  @action.bound
  public async syncCurrentThemeImages(variant: ThemeLayoutEnum): Promise<boolean> {
    if (this.syncingCurrentThemeImages) {
      return false
    }

    const isBackdrop = variant === ThemeLayoutEnum.BACKDROP
    const current = isBackdrop ? this.backdropConfig : this.showcaseConfig

    if (!current) {
      return false
    }

    this.syncingCurrentThemeImages = true

    try {
      // 先用 toJS 取得脱离 observable 的纯对象快照再剪枝，避免直接在 observable 代理上操作；
      // 剪枝结果是全新引用，重新赋值给 observable 才能让 MobX 通知组件（编辑器/预览）更新。
      const snapshot = toJS(current)
      const { payload: prunedPayload, modified } = await pruneMissingShopifyMediaImagesInThemePayload(
        snapshot,
      )

      if (!modified) {
        return false
      }

      runInAction(() => {
        if (isBackdrop) {
          this.backdropConfig = prunedPayload
        } else {
          this.showcaseConfig = prunedPayload
        }
      })

      return true
    } finally {
      this.syncingCurrentThemeImages = false
    }
  }

  /**
   * 就地移除当前内存配置中引用了指定 gid（已知刚被删除）的图片引用。
   * 不调用 /sync、不调用 save：仅修改内存 data（active 数据）；基线（元数据）由调用方按需同步。
   * @returns 是否发生了移除（true 时调用方需相应处理基线，确保不误触发全局保存）
   */
  @action.bound
  public removeShopifyMediaImageRefs(variant: ThemeLayoutEnum, gids: string[]): boolean {
    const isBackdrop = variant === ThemeLayoutEnum.BACKDROP
    const current = isBackdrop ? this.backdropConfig : this.showcaseConfig

    if (!current) {
      return false
    }

    // 先用 toJS 取脱离 observable 的纯快照再处理，命中后整体替换以触发 MobX 通知
    const snapshot = toJS(current)
    const { payload, modified } = removeShopifyMediaImageGidsFromPayload(snapshot, gids)

    if (!modified) {
      return false
    }

    runInAction(() => {
      if (isBackdrop) {
        this.backdropConfig = payload
      } else {
        this.showcaseConfig = payload
      }
    })

    return true
  }

  /**
   * common image 在应用内被删除时的订阅通道：定制页注册监听后，删除操作只需 emit gid，
   * 由页面据此清理「当前页面配置」中的引用并同步元数据，避免逐层透传回调到深层的 ImageUpload。
   */
  private commonImageDeletedListeners = new Set<(gid: string) => void>()

  public onCommonImageDeleted(listener: (gid: string) => void): () => void {
    this.commonImageDeletedListeners.add(listener)

    return () => {
      this.commonImageDeletedListeners.delete(listener)
    }
  }

  public emitCommonImageDeleted(gid: string): void {
    if (!gid) {
      return
    }
    this.commonImageDeletedListeners.forEach((listener) => listener(gid))
  }
}
